import { RaterParticipantCoverageDetails } from '@converge/graphql';

export interface SubCoverageGroupMember {
  subCoverageUuid: string;
  name: string;
  subLimit: number | null | undefined;
}

export interface SubCoverageGroupWarning {
  subCoverageUuid: string;
  groupName: string;
  subLimit: number | null | undefined;
  otherLimits: (number | null | undefined)[];
  members: SubCoverageGroupMember[];
}

/**
 * Detects sub-coverages that share the same participantCoverageName (alias)
 * within a single coverage, and flags those whose sub-limits differ from each
 * other. Grouping is scoped to one coverage: sub-coverages under different
 * coverages never group together, even when they share an alias.
 */
export function getSubCoverageGroupWarnings(
  coverages: RaterParticipantCoverageDetails[]
): Map<string, SubCoverageGroupWarning> {
  const warnings = new Map<string, SubCoverageGroupWarning>();

  const groups = new Map<
    string,
    {
      alias: string;
      subCoverageUuid: string;
      name: string;
      subLimit: number | null | undefined;
    }[]
  >();

  for (const coverage of coverages) {
    // Without a coverage uuid we cannot scope the group to its parent coverage;
    // a blank fallback would collapse every uuid-less coverage into one key and
    // re-introduce cross-coverage grouping, so skip such coverages entirely.
    if (!coverage.uuid) continue;

    for (const subCoverage of coverage.subCoverages) {
      const alias = subCoverage.quote?.participantCoverageName?.trim() ?? '';
      if (!alias || !subCoverage.quote?.included) continue;

      // Scope the group key to the parent coverage so an alias shared across
      // different coverages is never grouped (or validated) together.
      const groupKey = `${coverage.uuid}::${alias}`;

      const entry = {
        alias,
        subCoverageUuid: subCoverage.uuid ?? '',
        name: subCoverage.name ?? '',
        subLimit: subCoverage.quote?.subLimit,
      };

      const existing = groups.get(groupKey);
      if (existing) {
        existing.push(entry);
      } else {
        groups.set(groupKey, [entry]);
      }
    }
  }

  for (const [, members] of groups.entries()) {
    const alias = members[0].alias;
    if (members.length < 2) continue;

    const limits = members.map((m) => m.subLimit);
    const uniqueLimits = new Set(limits.map((l) => l ?? null));

    if (uniqueLimits.size <= 1) continue;

    const memberDetails: SubCoverageGroupMember[] = members.map((m) => ({
      subCoverageUuid: m.subCoverageUuid,
      name: m.name,
      subLimit: m.subLimit,
    }));

    for (const member of members) {
      const otherLimits = members
        .filter((m) => m.subCoverageUuid !== member.subCoverageUuid)
        .map((m) => m.subLimit);

      warnings.set(member.subCoverageUuid, {
        subCoverageUuid: member.subCoverageUuid,
        groupName: alias,
        subLimit: member.subLimit,
        otherLimits,
        members: memberDetails,
      });
    }
  }

  return warnings;
}
